import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
	Divider,
	Form,
	Message,
	Tab
} from 'semantic-ui-react';

import { log } from '../../../../../../services/utils';

import {
	pauseTemplate,
	unpauseTemplate
} from '../../../../../../api/nora/programs';

import { useAuthState } from '../../../../../../contexts/AuthContext';
import { useProgramState } from '../../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import Actions from '../../../../../Shared/Actions';
import FormDatePicker from '../../../../../Form/FormDatePicker';
import FormInput from '../../../../../Form/FormInput';
import FormSelect from '../../../../../Form/FormSelect';
import FormTextArea from '../../../../../Form/FormTextArea';
import FormErrors from '../../../../../Shared/FormErrors';
import TemplateTypeLabel from './TemplateTypeLabel';
import HelpModal from '../../../../../Shared/HelpModal';

const InfoTab = ( {
	errors,
	forceValidation,
	formData,
	formDataHasChanged,
	handleSave,
	resetFormData,
	updateField,
	validate
} ) => {
	const history = useHistory();
	const { id: programId } = useParams();

	const { user: { permissions } } = useAuthState();
	const {
		templateOccurrences,
		templateTypes,
		templateStatuses
	} = useProgramState();
	const { setApiErrorAlert } = useUIAction();

	const [ editable, setEditable ] = useState( !formData.id );
	const [ isSaving, setIsSaving ] = useState( false );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const templateTypeOptions = useMemo( () => templateTypes.map( ( {
		description,
		key,
		text,
		value
	} ) => ( {
		key,
		text,
		value,
		content: (
			<TemplateTypeLabel
				key={ key }
				description={ description }
				text={ text }
			/>
		)
	} ) ), [ templateTypes ] );

	const trigger = useMemo( () => {
		const type = templateTypes.find( ( { value } ) => value === formData?.type );

		if ( type ) {
			return (
				<TemplateTypeLabel
					key={ type.key }
					description={ type.description }
					text={ type.text }
				/>
			);
		}

		return null;
	}, [ templateTypes, formData?.type ] );

	const save = useCallback( async () => {
		const cb = () => {
			if ( !validate() ) {
				setEditable( false );
			}
		};

		setIsSaving( true );
		await handleSave( cb );
		setIsSaving( false );
	}, [
		handleSave,
		validate
	] );

	const saveAction = useMemo(
		() => ( {
			action: save,
			disable: !formDataHasChanged || isSaving,
			icon: 'save',
			permission: true,
			text: formData.id === 0
				? 'Save As New Template'
				: 'Save'
		} ),
		[
			formData.id,
			formDataHasChanged,
			isSaving,
			save
		]
	);

	const cancel = useCallback( () => {
		resetFormData();

		if ( formData.id === 0 ) {
			history.push( {
				pathname: `/operations/programs/${ programId }`,
				state: { activeIndex: 3 }
			} );
		} else {
			setEditable( false );
		}
	}, [
		formData.id,
		history,
		programId,
		resetFormData
	] );

	const cancelAction = useMemo(
		() => ( {
			action: cancel,
			icon: 'times',
			permission: true,
			text: 'Cancel'
		} ),
		[ cancel ]
	);

	const edit = useCallback( () => {
		setEditable( true );
	}, [] );

	// this is a hard coded use of the permission addition planned for 1.5
	const editAction = useMemo( () => ( {
		action: edit,
		disable: !permissions.editPrograms.canExecute || ( formData.locked && !permissions.editLockedProgramSubmissionTemplates.canExecute ),
		icon: formData.locked && !permissions.editLockedProgramSubmissionTemplates.canExecute
			? 'lock'
			: 'pencil',
		permission: permissions.editPrograms.isAvailable,
		text: 'Edit'
	} ), [
		edit,
		formData.locked,
		permissions
	] );

	const actions = useMemo(
		() => editable
			? [
				cancelAction,
				saveAction
			]
			: [ editAction ],
		[
			cancelAction,
			editable,
			saveAction,
			editAction
		]
	);

	const handlePause = useCallback( () => {
		try {
			if ( formData.isPaused ) {
				unpauseTemplate( programId, formData.id );
			} else {
				pauseTemplate( programId, formData.id );
			}

			updateField( {
				target: {
					name: 'isPaused',
					value: !formData.isPaused
				}
			} );
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		formData.id,
		formData.isPaused,
		programId,
		setApiErrorAlert,
		updateField
	] );

	return (
		<Tab.Pane as="div">
			{ log( 'infoTab' ) }
			<Actions actions={ actions } />
			<div className="contact-form-container">
				<Form
					autoComplete="off"
					error={ forceValidation && validate() }
				>
					<Form.Group>
						<Form.Field>
							<label><HelpModal id={ 13 } triggerPosition="left" triggerSize="small" />Pause/Unpause</label>
							<Form.Checkbox
								toggle
								checked={ formData.isPaused }
								size="mini"
								onClick={ handlePause }
							/>
						</Form.Field>
					</Form.Group>

					<Form.Group>
						<FormSelect
							errors={ errors.status }
							fieldLabel="Status"
							forceValidation={ forceValidation }
							helpId={ editable ? 14 : null }
							name="status"
							onChange={ updateField }
							options={ templateStatuses }
							readOnly={ !editable }
							required
							value={ formData.status || '' }
							width={ 4 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormInput
							errors={ errors.name }
							fieldLabel="Name"
							forceValidation={ forceValidation }
							helpId={ editable ? 15 : null }
							maxLength={ 255 }
							name="name"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.name }
							width={ 8 }
						/>
					</Form.Group>

					<Form.Group>
						<FormSelect
							errors={ errors.type }
							fieldLabel="Type"
							forceValidation={ forceValidation }
							helpId={ editable ? 16 : null }
							name="type"
							onChange={ e => {
								if ( e.target.value === 100 ) {
									updateField( { target: { name: 'layout', value: [] } } );
								}

								updateField( e );
							} }
							options={ templateTypeOptions }
							readOnly={ !editable }
							required
							trigger={ trigger }
							value={ formData.type || '' }
							width={ 8 }
						/>
					</Form.Group>

					{ formData?.type === 101
						? <Form.Group>
							<FormSelect
								errors={ errors.type }
								fieldLabel="Occurrence"
								forceValidation={ forceValidation }
								helpId={ editable ? 17 : null }
								name="occurrence"
								onChange={ updateField }
								options={ templateOccurrences }
								readOnly={ !editable }
								required
								value={ formData.occurrence || '' }
								width={ 4 }
							/>
							{/* <FormSelect
								errors={ errors.reportingRequirement }
								fieldLabel="Reporting Requirement"
								forceValidation={ forceValidation }
								name="reportingRequirement"
								onChange={ updateField }
								options={ templateRequirements }
								readOnly={ !editable }
								required
								value={ formData.reportingRequirement || '' }
								width={ 4 }
							/> */}
						</Form.Group>
						: null
					}

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormDatePicker
							errors={ errors.open }
							fieldLabel={
								formData.type === 99 || formData.type === 100
									? 'Start'
									: formData.type === 98
										? 'Available'
										: 'Open'
							}
							forceValidation={ forceValidation }
							helpId={ editable ? 18 : null }
							maxLength={ 255 }
							name="open"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.open || '' }
							width={ 4 }
						/>
						{ formData.type !== 98
							? <FormDatePicker
								errors={ errors.due }
								fieldLabel={
									formData.type === 99 || formData.type === 100
										? 'End'
										: 'Due' }
								forceValidation={ forceValidation }
								helpId={ editable ? 19 : null }
								maxLength={ 255 }
								name="due"
								onChange={ updateField }
								readOnly={ !editable }
								required
								value={ formData.due || '' }
								width={ 4 }
							/>
							: null
						}
						{/* <FormDatePicker
							errors={ errors.close }
							fieldLabel="Close"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="close"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.close || '' }
							width={ 4 }
						/> */}
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormTextArea
							errors={ errors.description }
							fieldLabel="Description"
							forceValidation={ forceValidation }
							helpId={ editable ? 20 : null }
							name="description"
							onChange={ updateField }
							readOnly={ !editable }
							required
							rows={ 6 }
							value={ formData.description || '' }
							width={ 16 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormTextArea
							errors={ errors.introduction}
							fieldLabel={
								formData.type === 100
									? 'Message'
									: 'Instructions'
							}
							forceValidation={ forceValidation }
							helpId={ editable ? 21 : null }
							name="introduction"
							onChange={ updateField }
							readOnly={ !editable }
							required
							rows={ 6 }
							value={ formData.introduction || '' }
							width={ 16 }
						/>
					</Form.Group>
					<FormErrors mapFormErrors={ mapFormErrors } />
				</Form>
			</div>
		</Tab.Pane>
	);
};

InfoTab.propTypes = {
	errors: PropTypes.object,
	forceValidation: PropTypes.bool,
	formData: PropTypes.object,
	formDataHasChanged: PropTypes.bool,
	handleSave: PropTypes.func,
	resetFormData: PropTypes.func,
	updateField: PropTypes.func,
	validate: PropTypes.func
};

export default memo( InfoTab );
